import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, getDispatcherToken } from './DispatcherApp';
import shonanPalmLogo from '../../assets/shonan-palm-gold-transparent.png';
import { getTokyoDateKey } from '../../utils/tokyoDate';
import EditScheduleModal from './EditScheduleModal';
import { Pencil } from 'lucide-react';
import { cancelConfirmation, showOperationFeedback } from './scheduleOperation';

export default function DispatcherSchedules({ dispatcher, onLogout }) {
  const [schedules, setSchedules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const requestVersion = useRef(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCancelled, setShowCancelled] = useState(true);

  const fetchSchedules = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setLoadError('');
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule?${params}`, {
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (!response.ok) throw new Error(response.status === 401 ? '登录已失效，请重新登录' : '订单加载失败，请重试');
      const items = await response.json();
      if (!Array.isArray(items)) throw new Error('订单数据格式异常，请重试');
      if (version === requestVersion.current) setSchedules(items);
    } catch (err) {
      if (version === requestVersion.current) { setSchedules([]); setLoadError(err.message || '网络异常，请重试'); }
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/dispatcher/drivers`, {
      headers: { Authorization: `Bearer ${getDispatcherToken()}` },
    }).then(response => response.ok ? response.json() : []).then(setDrivers).catch(() => setDrivers([]));
  }, []);

  const handleCancel = async (scheduleId) => {
    const schedule = schedules.find(item => item.id === scheduleId);
    if (!schedule || !window.confirm(cancelConfirmation(schedule))) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule/${scheduleId}/cancel?expected_revision=${schedule.revision ?? 0}${schedule.updated_at ? `&expected_updated_at=${encodeURIComponent(schedule.updated_at)}` : ''}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (response.ok) {
        showOperationFeedback(await response.json());
        fetchSchedules();
      } else {
        const data = await response.json();
        alert(data.detail || '取消失败');
      }
    } catch (err) {
      alert('取消失败');
    }
  };

  const today = getTokyoDateKey();

  const filteredSchedules = showCancelled
    ? schedules
    : schedules.filter(s => s.status === 'active');

  // 状态标签颜色
  const getStatusLabel = (schedule) => {
    if (schedule.status === 'cancelled') {
      return { text: '已取消', color: 'premium-chip-destructive' };
    }
    if (schedule.date === today) {
      return { text: '今天', color: 'premium-chip-warning' };
    }
    if (schedule.date > today) {
      return { text: '将来', color: 'premium-chip-success' };
    }
    return { text: '过去', color: 'premium-chip' };
  };

  return (
    <div className="dispatcher-luxury-portal min-h-screen">
      {/* Header */}
      <header className="dispatcher-luxury-header border-b border-[#d8b76a]/22">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="shonan-compact-brand">
              <div className="shonan-compact-brand-mark">
                <img
                  src={shonanPalmLogo}
                  alt="Shonan International"
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="shonan-compact-brand-copy">
                <p className="shonan-compact-brand-title">SHONAN INTERNATIONAL</p>
                <p className="shonan-compact-brand-subtitle">Private Transfer & Charter</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-[#d8b76a]/22 bg-black/35 px-3 py-1.5 text-sm text-[#f3d98b]">{dispatcher?.name}</span>
              <a
                href="/internal"
                className="dispatcher-luxury-subtle-button px-3 py-1.5 text-xs"
                data-testid="dispatcher-schedules-internal-entry-link"
              >
                车辆司机管理后台
              </a>
              <button
                onClick={onLogout}
                className="dispatcher-luxury-subtle-button px-3 py-1.5 text-xs text-white/68"
              >
                退出
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <div className="max-w-7xl mx-auto px-4 pb-3">
          <nav className="dispatcher-luxury-toolbar flex gap-2">
            <Link
              to="/dispatcher"
              className="dispatcher-luxury-tab text-xs"
            >
              排班总览
            </Link>
            <Link 
              to="/dispatcher/schedules"
              className="dispatcher-luxury-tab dispatcher-luxury-tab-active text-xs"
            >
              我的追加
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-5">
        {/* Filters */}
        <div className="premium-card p-4 mb-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-muted-foreground">从:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="premium-input !px-2 !py-1.5 !text-xs"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-muted-foreground">到:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="premium-input !px-2 !py-1.5 !text-xs"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="premium-input !px-2 !py-1.5 !text-xs"
              >
                <option value="">全部状态</option>
                <option value="active">有效</option>
                <option value="cancelled">已取消</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  className="w-3.5 h-3.5 text-muted-foreground rounded"
                />
                <span className="text-xs text-muted-foreground">显示已取消</span>
              </label>
              <span className="text-xs text-muted-foreground">
                共 <span className="font-medium text-foreground">{filteredSchedules.length}</span> 条
              </span>
            </div>
          </div>
        </div>

        {/* Schedule List */}
        {loadError ? <div role="alert" className="p-6 text-destructive">{loadError} <button onClick={fetchSchedules} className="underline">重新加载</button></div> : loading ? (
          <div className="premium-card p-8 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-input border-t-primary rounded-full mx-auto"></div>
            <p className="text-muted-foreground mt-3 text-sm">加载中...</p>
          </div>
        ) : filteredSchedules.length === 0 ? (
          <div className="premium-card p-8 text-center">
            <div className="w-12 h-12 bg-surface rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-foreground">暂无追加记录</h3>
            <p className="text-muted-foreground mt-1 text-xs">您还没有创建任何追加排班</p>
            <Link
              to="/dispatcher"
              className="inline-block mt-3 premium-button bg-primary text-primary-foreground text-xs hover:brightness-110"
            >
              去添加排班
            </Link>
          </div>
        ) : (
          <div className="premium-card overflow-hidden">
            <div className="divide-y divide-gray-100">
              {filteredSchedules.map((schedule) => {
                const statusLabel = getStatusLabel(schedule);
                const isCancelled = schedule.status === 'cancelled';
                const isPast = schedule.date < today;
                const isToday = schedule.date === today;

                return (
                  <div
                    key={schedule.id}
                    className={`p-3 hover:bg-surface ${
                      isCancelled ? 'premium-chip-destructive bg-transparent opacity-70' :
                      isPast ? 'bg-surface/50 opacity-70' :
                      isToday ? 'border-l-4 border-l-warning' : ''
                    }`}
                    data-testid={`my-schedule-${schedule.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          {/* 来源标签 */}
                          <span className="premium-chip">调度追加</span>
                          {/* 状态标签 */}
                          <span className={statusLabel.color}>{statusLabel.text}</span>
                          <span className="font-medium text-foreground text-sm">
                            {schedule.date} {schedule.time}
                          </span>
                          <span className="premium-chip">
                            {schedule.price_amount == null ? '金额无查看权限' : `¥${schedule.price_amount.toLocaleString()} ${schedule.price_currency}`}
                          </span>
                        </div>

                        <div className="text-xs text-muted-foreground mb-1.5">
                          <span className="font-medium">司机: {schedule.driver_name}</span>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-success">●</span>
                            <span>{schedule.pickup_location || '-'}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-destructive">●</span>
                            <span>{schedule.dropoff_location || '-'}</span>
                          </div>
                        </div>

                        {schedule.notes && (
                          <p className="text-xs text-muted-foreground mt-1.5">备注: {schedule.notes}</p>
                        )}

                        <p className="text-xs text-muted-foreground mt-1.5">
                          创建于: {new Date(schedule.created_at).toLocaleString()}
                        </p>
                      </div>

                      <button type="button" title="查看或编辑订单" aria-label="查看或编辑订单" onClick={() => setEditing({ ...schedule, source_type: 'dispatcher_extra' })} className="premium-button text-xs px-2.5 py-1.5 text-primary">
                        <Pencil size={16} />
                      </button>
                      {!isCancelled && dispatcher?.permissions?.can_cancel && (
                        <button
                          onClick={() => handleCancel(schedule.id)}
                          className="premium-button bg-destructive/15 text-destructive hover:bg-destructive/25 text-xs px-2.5 py-1.5"
                        >
                          取消
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
      {editing && <EditScheduleModal schedule={editing} drivers={drivers} dispatcher={dispatcher} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchSchedules(); }} />}
    </div>
  );
}
